import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import styled from "styled-components";
import { Row, Col, Card, Table, Pagination, Button, Form, Spinner } from "react-bootstrap";
import PageTitle from "../../Components/PageTitle";
import { useTranslation } from "react-i18next";
import { getProvisionalTripReportList } from "../../redux/actions/ProvisionalReportActions";
import { getMasterDriverList, getMasterVehicleList, getMasterVendorList, getAllUsersRoleWiseList } from "../../redux/actions/MasterActions";
import RoundedPagination from "../../Components/Paginate";
import FormInput from "../../Components/FormInput";
import { Link, useNavigate } from "react-router-dom";
import swal from 'sweetalert2';
import { toast } from "react-toastify";
import { logout } from "../../redux/actions/AuthActions";
import useDebounce from "../../hooks/useDebounce";
import { getDisplayDateTimeFormat } from '../../util/generalUtills'
import { CSVLink } from 'react-csv';
import { addDays, format } from "date-fns";
import DatePicker from "react-datepicker";
import Select from "react-dropdown-select";
import Preloader from "../../Components/Preloader";
import PrintTable from "../../Components/PrintTable";
import ExportToExcel from "../../Components/ExportToExcel";


const Dashboard = () => {
  const { t } = useTranslation();
  const tableRef = useRef();
  const [swalProps, setSwalProps] = useState({});
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    AuthReducer: { auth, errors, loading },
    provisionalReportReducer: {
      provisionalTripReportLists,
      reportPermissions,
      loadingReport,
      errorsReport,
      reportCount,
    },
    masterReducer: {
      masterVehicleLists,
      masterDriverLists,
      masterVendorLists,
      masterUsersRoleWiseLists,
      loadingMaster,
      errorsMaster,
    },
  } = useSelector((state) => state);
  const [page, setPage] = useState(1);
  const [paginate, setPaginate] = useState(10);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState('desc');
  const indexOfLastItem = (page + 1) * paginate;
  const indexOfFirstItem = indexOfLastItem - paginate;
  const [dateRange, setDateRange] = useState([new Date(), new Date()]);
  const [startDate, endDate] = dateRange;
  const [jenId, setJenId] = useState("");
  const [vendorId, setVendorId] = useState("");
  const [vehicleId, setVehicleId] = useState("");
  const [driverId, setDriverId] = useState("");
  const [validated, setValidated] = useState(false);
  const [roleCode] = useState("JUNIOR_ENGINEER");

  useEffect(() => {
    if (auth?.accessToken && roleCode) {
      dispatch(getAllUsersRoleWiseList(auth?.accessToken, roleCode));
    }
  }, [auth?.accessToken, roleCode, dispatch]);

  useEffect(() => {
    if (auth?.accessToken) {
      dispatch(getMasterVendorList(auth?.accessToken, jenId));
    }
  }, [auth?.accessToken, jenId, dispatch]);

  useEffect(() => {
    dispatch(getMasterVehicleList(auth?.accessToken, jenId, vendorId));
    dispatch(getMasterDriverList(auth?.accessToken, jenId, vendorId));
  }, [jenId, vendorId, auth?.accessToken, dispatch])

  const fetchTripReportList = useCallback(() => {
    if (auth?.accessToken) {
      const formattedStartDate = startDate ? format(new Date(startDate), 'yyyy-MM-dd') : '';
      const formattedEndDate = endDate ? format(new Date(endDate), 'yyyy-MM-dd') : '';
      dispatch(getProvisionalTripReportList(auth?.accessToken, paginate, page, search, sort, jenId, vendorId, vehicleId, driverId, formattedStartDate, formattedEndDate));
    }
  }, [auth?.accessToken, paginate, page, search, sort, jenId, vendorId, vehicleId, driverId, startDate, endDate, dispatch]);

  useDebounce(fetchTripReportList, 500, [auth, paginate, page, search, sort, jenId, vendorId, vehicleId, driverId, startDate, endDate]);

  const csvHead = [['SR No.', 'Booking ID', 'Pickup Address', 'Drop Address',
    'Booking Date', 'Name', 'Mobile', 'Booking Status', 'Tanker Number',
    'Driver Name', 'Driver Mobile', 'Distance', 'Travelled', 'Water (K.L.)', 'Amount']
  ]
  const csvData = provisionalTripReportLists.map((cv, index) => [
    index + 1,
    cv?.bookingId || '',
    cv?.hydrantName || '',
    cv?.destinationName || '',
    cv?.bookingDate || '',
    cv?.name || '',
    cv?.mobile || '',
    cv?.taxiStatus || '',
    cv?.vehicleNumber || '',
    cv?.driverName || '',
    cv?.driverMobile || '',
    cv?.totalDistance || 0,
    cv?.travelledDistance || 0,
    cv?.fillingVolume || 0,
    cv?.totalTripAmount || 0
  ]);
  const exportData = csvHead.concat(csvData);

  useEffect(() => {
    if (errors) {
      if (errors?.status === 401) {
        dispatch(logout())
        toast.error(errors?.data?.message);
        navigate('/login');
        window.location.reload();
      }
    }

  }, [errors, dispatch, navigate])



  const handleSubmit = (event) => {
    const form = event.currentTarget;
    if (form.checkValidity() === false) {
      event.preventDefault();
      event.stopPropagation();
    }
    setValidated(true);
    const formattedStartDate = startDate ? format(new Date(startDate), 'yyyy-MM-dd') : '';
    const formattedEndDate = endDate ? format(new Date(endDate), 'yyyy-MM-dd') : '';
    dispatch(getProvisionalTripReportList(auth?.accessToken, paginate, page, search, sort, jenId, vendorId, vehicleId, driverId, formattedStartDate, formattedEndDate));
  };

  const handlePaginateChange = (e) => {
    const value = e.target.value === "All" ? reportCount : parseInt(e.target.value);
    setPaginate(value);
    setPage(1);
  };

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
  };

  const transformedVehicleOptions = useMemo(() =>
    masterVehicleLists.map(vehicle => ({
      ...vehicle,
      concatenatedLabel: `${vehicle.vehicle_number} - ${vehicle.vehicle_model}`
    })),
    [masterVehicleLists]
  );

  const transformedDriverOptions = useMemo(() =>
    masterDriverLists.map(vehicle => ({
      ...vehicle,
      concatenatedLabel: `${vehicle.driver_id} - ${vehicle.driver_name}`
    })),
    [masterDriverLists]
  );

  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      borderColor: errors?.data?.state_code ? 'red' : base.borderColor,
      '&:hover': {
        borderColor: errors?.data?.state_code ? 'red' : base.borderColor,
      }
    }),
  };

  return (
    <>
      <PageTitle
        breadCrumbItems={[
          { label: t("Provisional Trip Report"), path: "/Provisional-Trip-Report", active: true },
        ]}
        title={t("Provisional Trip Report")}
      />

      <Row>
        <Col>
          {(reportPermissions?.filter) && (
            <>
              <Card>
                <Card.Body>
                  <Row>
                    <Col lg={2}>
                      <Form.Group className="mb-3">
                        <Form.Label>{t("J.E.N.")}</Form.Label>
                        <Select
                          options={masterUsersRoleWiseLists}
                          values={masterUsersRoleWiseLists?.filter(name => name.id === jenId)}
                          name="jen_id"
                          labelField={"name"}
                          valueField={"id"}
                          searchable={true}
                          searchBy="name"
                          dropdownHeight="300px"
                          separator={true}
                          onChange={(selectedData) => {
                            setJenId(selectedData[0]?.id || "");
                            setVendorId(""); // Reset vendor when JEN changes
                            setVehicleId(""); // Reset vehicle when JEN changes
                            setDriverId(""); // Reset driver when JEN changes
                          }}
                          styles={customSelectStyles}
                        />
                        {errorsMaster?.data?.jen_id && (
                          <div className="invalid-feedback d-block">
                            <p className="text-danger">{errorsMaster?.data?.jen_id}</p>
                          </div>
                        )}
                      </Form.Group>
                    </Col>

                    <Col lg={2}>
                      <Form.Group className="mb-3">
                        <Form.Label>{t("Vendor")}</Form.Label>
                        <Select
                          options={masterVendorLists}
                          values={masterVendorLists?.filter(name => name.vendorId === vendorId)}
                          name="vendor_id"
                          labelField={"vendorName"}
                          valueField={"vendorId"}
                          searchBy={"vendorName"}
                          onChange={(selectedData) => setVendorId(selectedData[0]?.vendorId)}
                          styles={customSelectStyles}
                        />

                        {errorsMaster?.data?.vendor_id && (
                          <div className="invalid-feedback d-block">
                            <p className="text-danger">{errorsMaster?.data?.vendor_id}</p>
                          </div>
                        )}
                      </Form.Group>
                    </Col>

                    <Col lg={2}>
                      <Form.Group className="mb-3">
                        <Form.Label>{t("Vehicle")}</Form.Label>
                        <Select
                          options={transformedVehicleOptions}
                          values={transformedVehicleOptions?.filter(option => option.vehicleId === vehicleId)}
                          name="vehicle_id"
                          labelField={"vehicleNumber"}
                          valueField={"vehicleId"}
                          searchBy={"vehicleNumber"}
                          onChange={(selectedData) => setVehicleId(selectedData[0]?.vehicleId)}
                          styles={customSelectStyles}
                        />

                        {errorsMaster?.data?.vehicle_id && (
                          <div className="invalid-feedback d-block">
                            <p className="text-danger">{errorsMaster?.data?.vehicle_id}</p>
                          </div>
                        )}
                      </Form.Group>
                    </Col>

                    <Col lg={2}>
                      <Form.Group className="mb-3">
                        <Form.Label>{t("Driver")}</Form.Label>
                        <Select
                          options={transformedDriverOptions}
                          values={transformedDriverOptions?.filter(option => option.driverId === driverId)}
                          name="driver_id"
                          labelField={"driverName"}
                          valueField={"driverId"}
                          searchBy={"driverName"}
                          onChange={(selectedData) => setDriverId(selectedData[0]?.driverId)}
                          styles={customSelectStyles}
                        />

                        {errorsMaster?.data?.driverId && (
                          <div className="invalid-feedback d-block">
                            <p className="text-danger">{errorsMaster?.data?.driverId}</p>
                          </div>
                        )}
                      </Form.Group>
                    </Col>

                    <Col lg={2}>
                      <Form.Group className="mb-3">
                        <Form.Label>{t("Date")}</Form.Label>
                        <DatePicker
                          selectsRange={true}
                          dateFormat="yyyy-MM-dd"
                          startDate={startDate}
                          endDate={endDate}
                          maxDate={addDays(new Date(), 1)}
                          todayButton="Today"
                          showMonthDropdown
                          showYearDropdown
                          dropdownMode="select"
                          className="form-control"
                          onChange={(update) => {
                            if (update.length === 2) {
                              const date1 = new Date(update[0]);
                              date1.setHours(12, 0, 0, 0);
                              setDateRange([date1, null]);
                              if (update[1] != null) {
                                const date2 = new Date(update[1] || update[0]);
                                date2.setHours(12, 0, 0, 0);
                                setDateRange([date1, date2]);
                              }
                            }
                          }}
                          withPortal
                        />
                        {errorsMaster?.data?.startDate && (
                          <div className="invalid-feedback d-block">
                            <p className="text-danger">{errorsMaster?.data?.startDate}</p>
                          </div>
                        )}
                        {errorsMaster?.data?.endDate && (
                          <div className="invalid-feedback d-block">
                            <p className="text-danger">{errorsMaster?.data?.endDate}</p>
                          </div>
                        )}
                      </Form.Group>

                    </Col>

                    <Col lg={2} >
                      <div className="mb-3" style={{ marginTop: '1.6rem' }}>
                        {loadingReport ?
                          <Button color="primary" disabled>
                            <Spinner
                              className="spinner-border-sm me-1"
                              tag="span"
                              color="white"
                            />
                            Please wait ...
                          </Button>
                          :
                          <Button type="button" onClick={handleSubmit} disabled={loadingReport}>
                            Submit
                          </Button>
                        }
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </>
          )}

          <Card>
            <Card.Body>
              <div className="mb-2">
                <Row>
                  <Col lg={3}>
                    {(reportPermissions?.filter) && (
                      <>
                        <div className="d-flex align-items-center">
                          <input
                            type="search"
                            placeholder="Search..."
                            className="form-control ms-1"
                            autoComplete="off"
                            value={search}
                            onChange={handleSearchChange}
                          />
                        </div>
                      </>
                    )}
                  </Col>
                  <Col lg={9}>
                    <div className="d-flex justify-content-end align-items-center gap-2">
                      {(reportPermissions?.print) && (
                        <PrintTable
                          tableId="table-to-print"
                          logoUrl="https://admin.brainbird.co.in/logo.jpg"
                          title=""
                          removeLastColumn={true}
                        />
                      )}
                      {(reportPermissions?.excel) && (
                        <ExportToExcel tableRef={tableRef} filename="data.xlsx" />
                      )}
                    </div>
                  </Col>
                </Row>
              </div>

              <div style={{ overflowY: 'auto', maxHeight: '500px', border: "1px solid #dee2e6" }}>
                <Table ref={tableRef} className="mb-0 table-bordered"
                  size="sm"
                  striped
                  responsive
                  id="table-to-print"
                  style={{ width: "100%", tableLayout: "auto" }}>
                  <thead style={{ position: "sticky", top: 0, backgroundColor: "#f8f9fa", zIndex: 1 }}>
                    <tr>
                      <th>SR No.</th>
                      <th>Booking ID</th>
                      <th>Pickup Address</th>
                      <th>Drop Address</th>
                      <th>Booking Date</th>
                      <th>Name / Mobile</th>
                      <th>Booking Status</th>
                      <th>Tanker Number</th>
                      <th>Driver Name / Mobile</th>
                      <th>Distance</th>
                      <th>Travelled</th>
                      <th>Water (K.L.)</th>
                      <th>Amount</th>
                      <th>Vendor</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {provisionalTripReportLists.length === 0 ? (
                      <tr>
                        <td colSpan="15">No data found</td>
                      </tr>
                    ) : (
                      provisionalTripReportLists?.map?.((item, index) => (
                        <tr key={item.id}>
                          <th scope="row">{index + 1 + (page - 1) * paginate}</th>
                          <td>{item?.bookingId || ''}</td>
                          <td>{item?.hydrant?.name || ''}</td>
                          <td>{item?.destination?.name || ''}</td>
                          <td>{getDisplayDateTimeFormat(item?.bookingDate) || ''}</td>
                          <td>{item?.name || ''}<br />{item?.mobile || ''}</td>
                          <td>{item?.taxiStatus || ''}</td>
                          <td>{item?.vehicle?.vehicleNumber || ''}</td>
                          <td>{item?.driver?.driverName || ''}<br />{item?.driver?.driverMobile || ''}</td>
                          <td>{item?.totalDistance || 0}</td>
                          <td>{item?.travelledDistance || 0}</td>
                          <td>{item?.fillingVolume || 0}</td>
                          <td>{item?.totalTripAmount || 0}</td>
                          <td>{item?.vendor?.vendorName || ''}</td>
                          <td>
                            <Link to={`/Track-Details-Booking/${item?.id}`} className="badge bg-soft-warning text-dark m-1"
                              target="_blank"
                              rel="noopener noreferrer">
                              Track
                            </Link>

                            <Link to={`/Download-Slip/${item?.id}`} className="badge bg-soft-info text-dark m-1"
                              target="_blank"
                              rel="noopener noreferrer">
                              Download Slip
                            </Link>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>

              <div className="mt-3 d-lg-flex align-items-center text-center pb-1">
                <div className="d-inline-block me-3">
                  <label className="me-1">Display :</label>
                  <select className="form-select d-inline-block w-auto" onChange={handlePaginateChange}>
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </select>
                </div>
                <span className="me-3">Page <strong>{page}</strong> of <strong>{Math.ceil(reportCount / paginate)}</strong> </span>
                <RoundedPagination
                  currentPage={page}
                  totalPages={Math.ceil(reportCount / paginate)}
                  onPageChange={(pageNumber) => setPage(pageNumber)}
                />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      {loadingMaster && (<Preloader />)}
      {loadingReport && (<Preloader />)}
    </>
  );
};

export default Dashboard;
